import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { CreditCard, CheckCircle, Lock } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { paymentAPI } from '../services/api';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
const STRIPE_TEST_MODE = (process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '').startsWith('pk_test_');

// Must match IMMEDIATE_START_CONSENT_TEXT in backend/routes/payment_routes.py, which is what gets
// stored on the booking and repeated in the confirmation email.
const IMMEDIATE_START_CONSENT_TEXT =
  "I want the driver to start straight away. I understand that if I cancel after the driver has set off I'll pay for the service provided up to that point, and that once the job is complete I lose my right to cancel.";

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#ffffff',
      fontFamily: '"Inter", sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': { color: '#71717a' },
      backgroundColor: 'transparent',
      iconColor: '#E17431',
    },
    invalid: {
      color: '#ef4444',
      iconColor: '#ef4444',
    },
  },
};

const CheckoutForm = ({ serviceRequestId, amount, serviceName, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [immediateStart, setImmediateStart] = useState(false);
  const [cardError, setCardError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!acceptedTerms) {
      toast({ title: 'Terms Required', description: 'Please accept the Terms of Service to continue', variant: 'destructive' });
      return;
    }

    if (!immediateStart) {
      toast({ title: 'Confirmation Required', description: 'Please confirm you want the driver to start straight away', variant: 'destructive' });
      return;
    }

    if (!stripe || !elements) return;

    setIsProcessing(true);
    setCardError(null);

    try {
      // 1. Create payment intent on backend
      const intentData = await paymentAPI.createPaymentIntent(serviceRequestId, 'card', immediateStart);

      const { client_secret, payment_intent_id } = intentData;

      // 2. If mock mode (no real Stripe key on backend), skip card confirmation
      if (payment_intent_id?.startsWith('mock_pi_')) {
        await paymentAPI.confirmPayment(payment_intent_id, serviceRequestId);
        onSuccess();
        return;
      }

      // 3. Confirm real card payment with Stripe
      const { error, paymentIntent } = await stripe.confirmCardPayment(client_secret, {
        payment_method: { card: elements.getElement(CardElement) },
      });

      if (error) {
        setCardError(error.message);
        toast({ title: 'Payment Failed', description: error.message, variant: 'destructive' });
        return;
      }

      if (paymentIntent.status === 'succeeded') {
        await paymentAPI.confirmPayment(paymentIntent.id, serviceRequestId);
        onSuccess();
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Payment failed. Please try again.';
      setCardError(msg);
      toast({ title: 'Payment Error', description: msg, variant: 'destructive' });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Amount banner */}
      <div className="bg-gradient-to-r from-wheely-orange to-wheely-orange-dark rounded-xl p-6 text-center">
        <p className="text-orange-100 text-sm mb-1">Total Amount</p>
        <p className="text-white font-black text-5xl">£{amount}</p>
        <p className="text-orange-100 text-sm mt-2">{serviceName}</p>
      </div>

      {/* Card input */}
      <div className="space-y-3">
        <Label className="text-white text-sm font-medium flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-wheely-orange-light" /> Card Details
        </Label>
        <div className="bg-wheely-navy-800 border border-wheely-navy-700 rounded-lg px-4 py-4 focus-within:border-wheely-orange transition-colors">
          <CardElement options={CARD_ELEMENT_OPTIONS} onChange={() => setCardError(null)} />
        </div>
        {cardError && (
          <p className="text-red-400 text-sm flex items-center gap-1">
            <span>⚠</span> {cardError}
          </p>
        )}
        {STRIPE_TEST_MODE && (
          <p className="text-zinc-500 text-xs">Test card: 4242 4242 4242 4242 · Any future date · Any CVC</p>
        )}
      </div>

      {/* Terms */}
      <div className="flex items-start gap-3">
        <Checkbox
          id="terms-payment"
          checked={acceptedTerms}
          onCheckedChange={setAcceptedTerms}
          className="mt-0.5"
        />
        <Label htmlFor="terms-payment" className="text-sm text-zinc-400 cursor-pointer leading-relaxed">
          I agree to the{' '}
          <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-wheely-orange hover:text-wheely-orange-light underline" onClick={(e) => e.stopPropagation()}>
            Terms of Service
          </a>{' '}
          and authorise this payment
        </Label>
      </div>

      {/* Consumer Contracts Regulations 2013: express request for immediate service */}
      <div className="flex items-start gap-3">
        <Checkbox
          id="immediate-start"
          checked={immediateStart}
          onCheckedChange={setImmediateStart}
          className="mt-0.5"
        />
        <Label htmlFor="immediate-start" className="text-sm text-zinc-400 cursor-pointer leading-relaxed">
          {IMMEDIATE_START_CONSENT_TEXT}
        </Label>
      </div>

      <Button
        type="submit"
        disabled={isProcessing || !stripe}
        className="w-full bg-gradient-to-r from-wheely-orange to-wheely-orange-dark hover:from-wheely-orange-dark hover:to-orange-700 text-white font-semibold text-lg py-6 rounded-xl"
      >
        {isProcessing ? (
          <span className="flex items-center gap-2">
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Processing...
          </span>
        ) : (
          <span className="flex items-center gap-2">
            <Lock className="w-4 h-4" /> Pay £{amount} Securely
          </span>
        )}
      </Button>

      <p className="text-center text-zinc-500 text-xs flex items-center justify-center gap-1">
        <Lock className="w-3 h-3" /> Secured by Stripe · 256-bit encryption
      </p>
    </form>
  );
};

const Payment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { service, estimatedCost, serviceRequestId } = location.state || {};
  const [paymentComplete, setPaymentComplete] = useState(false);

  useEffect(() => {
    if (!service) navigate('/customer/dashboard', { replace: true });
  }, [service, navigate]);

  const handleSuccess = () => {
    setPaymentComplete(true);
    toast({ title: 'Payment Successful!', description: 'Your driver has been notified.' });
    setTimeout(() => navigate('/customer/dashboard'), 3000);
  };

  if (!service) return null;

  if (paymentComplete) {
    return (
      <div className="min-h-screen bg-wheely-navy-950 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-gradient-to-br from-wheely-navy-950 to-wheely-navy-900 border-wheely-orange/20">
          <CardContent className="pt-8 pb-8">
            <div className="text-center space-y-6">
              <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto animate-bounce">
                <CheckCircle className="w-14 h-14 text-white" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-white mb-2">Payment Successful!</h2>
                <p className="text-zinc-400">Your driver has been notified and is on the way.</p>
              </div>
              <div className="bg-wheely-navy-900 rounded-xl p-4 border border-wheely-navy-700">
                <p className="text-zinc-400 text-sm mb-1">Amount Paid</p>
                <p className="text-wheely-orange font-black text-4xl">£{estimatedCost}</p>
              </div>
              <p className="text-zinc-500 text-sm">Redirecting to dashboard...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-wheely-navy-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="bg-gradient-to-br from-wheely-navy-950 to-wheely-navy-900 border-wheely-orange/20">
          <CardHeader>
            <CardTitle className="text-white text-2xl flex items-center gap-2">
              <CreditCard className="w-6 h-6 text-wheely-orange-light" /> Complete Payment
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Elements stripe={stripePromise} options={{ locale: 'en-GB' }}>
              <CheckoutForm
                serviceRequestId={serviceRequestId}
                amount={estimatedCost}
                serviceName={service?.name}
                onSuccess={handleSuccess}
              />
            </Elements>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Payment;
